import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import type { RefObject } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import type { UseDictationResult } from './useDictation.ts';
import type { SpeakingErrorCode, UseSpeakingResult } from '../speaking/useSpeaking.ts';
import { useI18n } from '../../i18n/index.ts';
import { useDeviceClass } from '../../hooks/useDeviceClass.ts';
import { useVisualViewportInset } from '../../hooks/useVisualViewportInset.ts';
import {
  applyDictationInputAppearance,
  resolveDictationStyleSource,
} from './dictationStyleSource.ts';

export type DictationInputProps = {
  settings: Settings;
  dictation: UseDictationResult;
  speaking?: UseSpeakingResult;
  /** Overlay host that contains #mainSub / #secondSub (positioning parent). */
  overlayHostRef?: RefObject<HTMLElement | null>;
  /** Fired when dictation keyboard is up (video docks to top). Not for speaking / idle compose. */
  onComposeOpenChange?: (open: boolean) => void;
};

const KEYBOARD_COMPOSE_THRESHOLD_PX = 120;

function practiceSlotId(slot: 'main' | 'second'): 'mainSub' | 'secondSub' {
  return slot === 'main' ? 'mainSub' : 'secondSub';
}

/**
 * Compose chrome that must not dismiss the soft keyboard on iOS/Android.
 * Mirrors the dictation hint control: non-passive touchstart preventDefault +
 * optional refocus of the typing field. Uses span[role=button] so the control
 * itself never steals focus.
 */
function ComposeKeepFocusControl({
  testId,
  className,
  label,
  disabled,
  pressed,
  onAction,
  inputRef,
  refocusInput,
}: {
  testId: string;
  className: string;
  label: string;
  disabled?: boolean;
  pressed?: boolean;
  onAction: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** When true, restore focus to the dictation field after the action. */
  refocusInput?: boolean;
}) {
  const elRef = useRef<HTMLSpanElement | null>(null);
  const touchHandledRef = useRef(false);

  const focusField = useCallback(() => {
    if (!refocusInput || !inputRef) return;
    inputRef.current?.focus({ preventScroll: true });
    window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [inputRef, refocusInput]);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;

    const onTouchStart = (event: TouchEvent) => {
      event.preventDefault();
      if (disabled) return;
      touchHandledRef.current = true;
      onAction();
      focusField();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => el.removeEventListener('touchstart', onTouchStart);
  }, [disabled, onAction, focusField]);

  const keepFocus = useCallback((event: { preventDefault(): void }) => {
    event.preventDefault();
  }, []);

  return (
    <span
      ref={elRef}
      role="button"
      className={className}
      data-testid={testId}
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      aria-pressed={pressed}
      onMouseDown={keepFocus}
      onPointerDown={keepFocus}
      onClick={() => {
        if (disabled) return;
        if (touchHandledRef.current) {
          touchHandledRef.current = false;
          focusField();
          return;
        }
        onAction();
        if (refocusInput && inputRef) {
          inputRef.current?.focus({ preventScroll: true });
          window.requestAnimationFrame(() => {
            inputRef.current?.focus({ preventScroll: true });
          });
        }
      }}
    >
      {label}
    </span>
  );
}

/**
 * Hybrid dictation / speaking input:
 * - Desktop (D0): cue-adjacent invisible input (extension parity; speaking = shortcuts)
 * - Phone / tablet (D1–D4):
 *   - Dictation: compose pill + text field + cue nav (tight layout above keyboard)
 *   - Speaking / shadowing: compose pill + record / replay-take + cue nav (no text field)
 */
export function DictationInput({
  settings,
  dictation,
  speaking,
  overlayHostRef,
  onComposeOpenChange,
}: DictationInputProps) {
  const { t } = useI18n();
  const { dim, device } = useDeviceClass();
  const { keyboardInset, height: vvHeight, width: vvWidth, offsetTop, offsetLeft } =
    useVisualViewportInset();
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasCue, setHasCue] = useState(false);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);
  const [engaged, setEngaged] = useState(false);
  /** True only while the compose field holds focus (soft keyboard expected). */
  const [fieldFocused, setFieldFocused] = useState(false);

  const registerInput = dictation.registerInput;
  const isPractice = settings.practiceMode === 'dictation';
  const isSpeaking = settings.practiceMode === 'speaking';
  const isShadowing = settings.practiceMode === 'shadowing';
  /** Speaking-style compose: record/replay, no text field (speaking + shadowing). */
  const isRecordCompose = isSpeaking || isShadowing;

  // Touch / compact shells use compose chrome; desktop keeps cue-adjacent input.
  const preferCompose =
    device !== 'desktop' || keyboardInset > KEYBOARD_COMPOSE_THRESHOLD_PX;
  // Only open after explicit engage — never from visualViewport noise (URL bar etc.).
  const composeOpen = preferCompose && engaged;

  useEffect(() => {
    if (!dictation.visible) {
      setEngaged(false);
      setFieldFocused(false);
    }
  }, [dictation.visible]);

  useEffect(() => {
    // Top-dock only while the dictation field is focused (soft keyboard).
    // After blur / keyboard dismiss, compose can stay open — video returns to center.
    const typingDock = composeOpen && !isRecordCompose && fieldFocused;
    onComposeOpenChange?.(typingDock);
    return () => onComposeOpenChange?.(false);
  }, [composeOpen, isRecordCompose, fieldFocused, onComposeOpenChange]);

  useLayoutEffect(() => {
    // Mobile speaking/shadowing compose has no text field.
    // Otherwise register so focus/locker work (dictation compose + desktop cue path).
    const mountInput =
      dictation.visible &&
      !(isRecordCompose && preferCompose) &&
      (composeOpen || (!preferCompose && hasCue));
    registerInput(mountInput ? inputRef.current : null);
    return () => registerInput(null);
  }, [registerInput, hasCue, dictation.visible, preferCompose, composeOpen, isRecordCompose]);

  // Dictation compose: do not auto-focus the field (no soft keyboard until the user taps it).

  // Speaking compose: pause-at-end locker without opening the soft keyboard.
  // Shadowing keeps the record panel but continuous playback (no per-cue stop).
  useEffect(() => {
    if (!composeOpen || !isSpeaking) return undefined;
    dictation.onFocus();
    return undefined;
  }, [composeOpen, isSpeaking, dictation.onFocus]);

  const engageAndFocus = useCallback(() => {
    setEngaged(true);
    if (isShadowing) {
      return;
    }
    if (isSpeaking) {
      dictation.onFocus();
      // Desktop speaking still uses the invisible cue-adjacent field.
      if (!preferCompose) {
        window.setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 0);
      }
      return;
    }
    // Dictation compose: open the bar only — tap the field when ready to type.
    if (!preferCompose) {
      window.setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
    }
  }, [preferCompose, isSpeaking, isShadowing, dictation]);

  const endCompose = useCallback(() => {
    if (speaking?.isRecording) speaking.stopRecording();
    dictation.endTypingSession();
    inputRef.current?.blur();
    setFieldFocused(false);
    setEngaged(false);
  }, [dictation, speaking]);

  const onHintAction = useCallback(() => {
    dictation.onHint();
  }, [dictation.onHint]);

  const onNavPrev = useCallback(() => {
    dictation.navigateCue('prev');
  }, [dictation]);

  const onNavReplay = useCallback(() => {
    dictation.navigateCue('repeat');
  }, [dictation]);

  const onNavNext = useCallback(() => {
    dictation.navigateCue('next');
  }, [dictation]);

  const onRecordClick = useCallback(() => {
    if (!speaking) return;
    if (speaking.isRecording || speaking.isInitializing) {
      speaking.stopRecording();
      return;
    }
    void speaking.startRecording();
  }, [speaking]);

  const onReplayClick = useCallback(() => {
    speaking?.replayRecording();
  }, [speaking]);

  const speakingErrorMessage = (code: SpeakingErrorCode | null): string | null => {
    if (!code) return null;
    if (code === 'secure') return t('speakErrorSecure');
    if (code === 'permission') return t('speakErrorPermission');
    if (code === 'unsupported') return t('speakErrorUnsupported');
    return t('speakErrorUnknown');
  };

  // Cue click → engage (extension onClickSubContainer).
  useEffect(() => {
    if (!dictation.visible) return undefined;
    const host: EventTarget = overlayHostRef?.current ?? document;
    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest) return;
      if (target.closest('.unisubs-sub-drag-handle')) return;
      if (target.closest('[data-testid="dictation-compose"]')) return;
      if (target.closest('[data-testid="dictation-tap-pill"]')) return;
      const slotId = practiceSlotId(dictation.dictationSlot);
      if (!target.closest(`#${slotId}`)) return;
      engageAndFocus();
    };
    host.addEventListener('click', onClick, true);
    return () => host.removeEventListener('click', onClick, true);
  }, [dictation.visible, dictation.dictationSlot, engageAndFocus, overlayHostRef]);

  // Detect painted cue + cue-adjacent positioning when not in compose UI.
  useLayoutEffect(() => {
    if (!dictation.visible) {
      setHasCue(false);
      setBox(null);
      return undefined;
    }

    const sync = () => {
      const doc = overlayHostRef?.current?.ownerDocument ?? document;
      const slotId = practiceSlotId(dictation.dictationSlot);
      const subContainer = doc.getElementById(slotId);
      const topContainer =
        doc.getElementById('mainSub') || doc.getElementById('secondSub') || subContainer;
      const styleSource = resolveDictationStyleSource(subContainer);

      if (!styleSource || !topContainer) {
        setHasCue(false);
        setBox(null);
        return;
      }
      setHasCue(true);

      // Mobile speaking/shadowing has no cue-adjacent field to position.
      if (isRecordCompose && preferCompose) {
        setBox(null);
        return;
      }

      const input = inputRef.current;
      if (!input) return;

      if (composeOpen) {
        input.style.removeProperty('opacity');
        input.style.removeProperty('pointer-events');
        return;
      }

      applyDictationInputAppearance(input, styleSource);

      const host = overlayHostRef?.current;
      const hostRect = host?.getBoundingClientRect() ?? { left: 0, top: 0 };
      const cueRect = topContainer.getBoundingClientRect();
      const inputH = input.offsetHeight || 40;
      const inputW = input.offsetWidth || Math.min(Math.max(cueRect.width, 120), 320);
      const gap = 8;
      const left = cueRect.left - hostRect.left + cueRect.width / 2 - inputW / 2;
      const top =
        settings.dictationInputPosition === 'top'
          ? cueRect.top - hostRect.top - inputH - gap
          : cueRect.bottom - hostRect.top + gap;

      setBox({ left, top });

      if (dictation.inputValue.length > 0) {
        input.style.removeProperty('opacity');
      } else {
        input.style.opacity = '0';
      }
    };

    sync();
    const id = window.setInterval(sync, 200);
    return () => window.clearInterval(id);
  }, [
    dictation.visible,
    dictation.dictationSlot,
    dictation.inputValue,
    settings.dictationInputPosition,
    overlayHostRef,
    composeOpen,
    isRecordCompose,
    preferCompose,
  ]);

  if (!dictation.visible) return null;

  const placeholder =
    isRecordCompose ? t('dictationPlaceholderSpeaking') : t('dictationPlaceholder');
  const opacityHidden = !composeOpen && dictation.inputValue.length === 0;
  const overlayHost = overlayHostRef?.current;
  const pillLabel = isShadowing
    ? t('dictationTapToShadow')
    : isSpeaking
      ? t('dictationTapToSpeak')
      : t('dictationTapToType');

  const field = (
    <input
      ref={inputRef}
      className={composeOpen ? 'dictation-compose__field' : 'unisubs-dictation-input'}
      data-testid="dictation-field"
      type="text"
      value={dictation.inputValue}
      placeholder={composeOpen || !opacityHidden ? placeholder : ''}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      enterKeyHint="done"
      aria-label={placeholder}
      aria-hidden={opacityHidden ? true : undefined}
      onChange={(e) => dictation.onInput(e.target.value)}
      onCompositionStart={dictation.onCompositionStart}
      onCompositionEnd={(e) => dictation.onCompositionEnd(e.currentTarget.value)}
      onFocus={() => {
        setEngaged(true);
        setFieldFocused(true);
        dictation.onFocus();
      }}
      onBlur={() => {
        setFieldFocused(false);
        dictation.onBlur();
        if (!preferCompose) {
          dictation.endTypingSession();
        }
      }}
    />
  );

  const speakingControls = speaking ? (
    <div className="dictation-compose__speaking-wrap">
      <div className="dictation-compose__speaking" data-testid="speaking-controls">
        <ComposeKeepFocusControl
          testId="speaking-record"
          className={`dictation-compose__chip dictation-compose__speak-btn${speaking.isRecording ? ' dictation-compose__speak-btn--active' : ''}`}
          label={speaking.isRecording || speaking.isInitializing ? t('speakStop') : t('speakRecord')}
          pressed={speaking.isRecording}
          disabled={speaking.isInitializing}
          onAction={onRecordClick}
        />
        <ComposeKeepFocusControl
          testId="speaking-replay"
          className="dictation-compose__chip dictation-compose__speak-btn"
          label={t('speakReplay')}
          disabled={!speaking.hasRecording || speaking.isRecording}
          onAction={onReplayClick}
        />
      </div>
      {speaking.lastError ? (
        <p className="dictation-compose__speak-error" data-testid="speaking-error" role="alert">
          {speakingErrorMessage(speaking.lastError)}
        </p>
      ) : null}
    </div>
  ) : null;

  const cueNav = (
    <div className="dictation-compose__nav" data-testid="practice-cue-nav">
      <ComposeKeepFocusControl
        testId="practice-nav-prev"
        className="dictation-compose__chip dictation-compose__nav-btn"
        label={t('practiceNavPrev')}
        onAction={onNavPrev}
        inputRef={inputRef}
        refocusInput={isPractice}
      />
      <ComposeKeepFocusControl
        testId="practice-nav-replay"
        className="dictation-compose__chip dictation-compose__nav-btn"
        label={t('practiceNavReplay')}
        onAction={onNavReplay}
        inputRef={inputRef}
        refocusInput={isPractice}
      />
      <ComposeKeepFocusControl
        testId="practice-nav-next"
        className="dictation-compose__chip dictation-compose__nav-btn"
        label={t('practiceNavNext')}
        onAction={onNavNext}
        inputRef={inputRef}
        refocusInput={isPractice}
      />
    </div>
  );

  // —— Compose / pill: dock layer matches visualViewport ——
  if (preferCompose) {
    const dockStyle: CSSProperties = composeOpen
      ? {
          position: 'fixed',
          top: offsetTop,
          left: offsetLeft,
          width: vvWidth || '100%',
          height: vvHeight || '100%',
          zIndex: 50,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }
      : {
          position: 'fixed',
          top: offsetTop,
          left: offsetLeft,
          width: vvWidth || '100%',
          height: vvHeight || '100%',
          zIndex: 50,
          pointerEvents: 'none',
          boxSizing: 'border-box',
        };
    const chrome = !composeOpen ? (
      <div className="dictation-vv-dock" style={dockStyle} data-testid="dictation-vv-dock">
        <button
          type="button"
          className="dictation-tap-pill"
          data-testid="dictation-tap-pill"
          data-dim={dim}
          data-practice-mode={settings.practiceMode}
          onClick={engageAndFocus}
        >
          {pillLabel}
        </button>
      </div>
    ) : (
      <div className="dictation-vv-dock" style={dockStyle} data-testid="dictation-vv-dock">
        <div
          className="dictation-compose"
          data-testid="dictation-compose"
          data-practice-mode={settings.practiceMode}
          data-dim={dim}
        >
          {isPractice ? (
            <div
              className="dictation-compose__band"
              data-testid="dictation-practice-band"
              data-dictation-slot={dictation.dictationSlot}
            >
              <div className="dictation-compose__lines">
                {(() => {
                  const maskLine = (
                    <p className="dictation-compose__mask" data-testid="dictation-mask-preview">
                      {dictation.maskPreview || '…'}
                    </p>
                  );
                  const companionLine = dictation.composeCompanion ? (
                    <p
                      className="dictation-compose__companion"
                      data-testid="dictation-compose-companion"
                    >
                      {dictation.composeCompanion}
                    </p>
                  ) : null;
                  // Match overlay order: main above second.
                  return dictation.dictationSlot === 'second'
                    ? <>{companionLine}{maskLine}</>
                    : <>{maskLine}{companionLine}</>;
                })()}
              </div>
              <div className="dictation-compose__actions">
                <ComposeKeepFocusControl
                  testId="dictation-hint-btn"
                  className="dictation-compose__chip"
                  label={t('shortcutDictationHint')}
                  onAction={onHintAction}
                  inputRef={inputRef}
                  refocusInput
                />
              </div>
            </div>
          ) : null}
          {cueNav}
          {isRecordCompose ? (
            <div className="dictation-compose__row dictation-compose__row--speaking">
              {speakingControls}
              <button
                type="button"
                className="dictation-compose__done"
                data-testid="dictation-done"
                onClick={endCompose}
              >
                {t('dictationDone')}
              </button>
            </div>
          ) : (
            <div className="dictation-compose__row">
              {field}
              <button
                type="button"
                className="dictation-compose__done"
                data-testid="dictation-done"
                onClick={endCompose}
              >
                {t('dictationDone')}
              </button>
            </div>
          )}
        </div>
      </div>
    );

    return (
      <>
        {createPortal(chrome, document.body)}
        <div
          data-testid="dictation-input"
          data-practice-mode={settings.practiceMode}
          data-layout={composeOpen ? 'compose' : 'compose-pill'}
          data-hidden={composeOpen ? 'false' : 'true'}
          aria-hidden
        />
      </>
    );
  }

  // —— Desktop: cue-adjacent invisible input (dictation + speaking; speaking uses shortcuts) ——
  if (!hasCue || !overlayHost) return null;

  return createPortal(
    <div
      className="unisubs-dictation-input-host"
      data-testid="dictation-input"
      data-practice-mode={settings.practiceMode}
      data-layout="cue"
      data-hidden={opacityHidden ? 'true' : 'false'}
      style={box ? { left: `${box.left}px`, top: `${box.top}px` } : undefined}
    >
      {field}
    </div>,
    overlayHost,
  );
}
